import React from 'react';
import type {
  UseFormSetValue,
  UseFormWatch,
  UseFormClearErrors,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form';

import { useTranslation } from '@/i18n';
import { Selector } from '@/components/ui/inputs/selector';
import { Switch, LabelInput } from '@/components/ui/inputs';
import { DatePicker } from '@/components/ui/filters';
import { FormLayout } from '@/components/layout';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';


// Campos del período compartidos por crear y editar. El componente solo necesita estos
// para la sección "Periodo"; los campos de empresa/holding solo se usan en modo crear.
interface MonthlyCutPeriodFields extends FieldValues {
  year: number | null;
  month: number | null;
  startDate: string;
  endDate: string;
}

// Subconjunto del formulario que el componente consume (genérico sobre el schema concreto).
export interface MonthlyCutGeneralFormSubset<T extends MonthlyCutPeriodFields> {
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  clearErrors: UseFormClearErrors<T>;
  errors: FieldErrors<T>;
}

interface MonthlyCutGeneralFormProps<T extends MonthlyCutPeriodFields> {
  form: MonthlyCutGeneralFormSubset<T>;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  // 'create': empresa/holding editables vía form. 'edit': empresa y holding en solo lectura.
  mode?: 'create' | 'edit';
  // Modo crear: opciones de empresa (selector editable).
  companyOptions?: { value: string; label: string }[];
  // Modo editar: id del corte (solo lectura).
  cutId?: number;
  // Modo editar: empresa del corte (solo lectura) y valor original del switch holding.
  companyName?: string;
  applyToAllCompanies?: boolean;
}

export function CreateMonthlyCutGeneralForm<T extends MonthlyCutPeriodFields>({
  form,
  yearOptions,
  monthOptions,
  mode = 'create',
  companyOptions = [],
  cutId,
  companyName,
  applyToAllCompanies,
}: MonthlyCutGeneralFormProps<T>): React.ReactElement {
  const { t: translate } = useTranslation();

  const isEdit = mode === 'edit';

  // En modo crear estos campos existen en el schema; en editar no se leen (empresa solo lectura).
  const watchedApplyToAll = isEdit
    ? Boolean(applyToAllCompanies)
    : Boolean(form.watch('applyToAllCompanies' as Path<T>));
  const watchedCompanyId = isEdit
    ? undefined
    : (form.watch('targetCompanyId' as Path<T>) as number | null | undefined);

  const year = form.watch('year' as Path<T>) as number | null | undefined;
  const month = form.watch('month' as Path<T>) as number | null | undefined;
  const startDate = form.watch('startDate' as Path<T>) as string | undefined;
  const endDate = form.watch('endDate' as Path<T>) as string | undefined;

  const errors = form.errors as FieldErrors<MonthlyCutPeriodFields> & {
    targetCompanyId?: { message?: string };
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  return (
    <FormLayout onSubmit={handleSubmit} columns={4} data-testid="create-monthly-cut-general-form">
      <FormLayout.Section title={translate('attendance:monthlyCutCreate.sections.cutData')}>
        {isEdit ? (
          <>
            <LabelInput
              label={translate('attendance:monthlyCutCreate.fields.id')}
              value={cutId ? String(cutId) : ''}
              readOnly
              disabled
              data-testid="monthly-cut-id-input"
            />
            <LabelInput
              label={translate('attendance:monthlyCutCreate.fields.company')}
              value={companyName ?? ''}
              readOnly
              disabled
              data-testid="monthly-cut-company-input"
            />
          </>
        ) : (
          <Selector
            label={translate('attendance:monthlyCutCreate.fields.company')}
            required
            options={companyOptions}
            value={watchedCompanyId ? String(watchedCompanyId) : undefined}
            onChange={(value) =>
              form.setValue(
                'targetCompanyId' as Path<T>,
                (normalizeNumericSelectValue(value) ?? null) as never,
                { shouldDirty: true },
              )}
            disabled={watchedApplyToAll}
            placeholder={translate('attendance:monthlyCutCreate.placeholders.company')}
            color={errors.targetCompanyId ? 'error' : 'default'}
            supportingText={errors.targetCompanyId?.message
              ? translate(errors.targetCompanyId.message)
              : undefined}
            data-testid="monthly-cut-company-selector"
          />
        )}
        <div className="flex items-center pt-7">
          <Switch
            label={translate('attendance:monthlyCutCreate.fields.applyToAllCompanies')}
            checked={watchedApplyToAll}
            disabled={isEdit}
            onChange={isEdit
              ? undefined
              : (checked) => {
                form.setValue('applyToAllCompanies' as Path<T>, checked as never, { shouldDirty: true });
                // En holding la empresa no es obligatoria: limpia el error si estaba marcado.
                if (checked) form.clearErrors('targetCompanyId' as Path<T>);
              }}
            data-testid="monthly-cut-apply-all-switch"
          />
        </div>
      </FormLayout.Section>

      <FormLayout.Section title={translate('attendance:monthlyCutCreate.sections.period')}>
        <Selector
          label={translate('attendance:monthlyCutCreate.fields.year')}
          required
          options={yearOptions}
          value={year ? String(year) : undefined}
          onChange={(value) =>
            form.setValue('year' as Path<T>, (normalizeNumericSelectValue(value) ?? null) as never, { shouldDirty: true })}
          placeholder={translate('attendance:monthlyCutCreate.placeholders.year')}
          color={errors.year ? 'error' : 'default'}
          supportingText={errors.year?.message
            ? translate(errors.year.message)
            : undefined}
          data-testid="monthly-cut-year-selector"
        />
        <Selector
          label={translate('attendance:monthlyCutCreate.fields.month')}
          required
          options={monthOptions}
          value={month ? String(month) : undefined}
          onChange={(value) =>
            form.setValue('month' as Path<T>, (normalizeNumericSelectValue(value) ?? null) as never, { shouldDirty: true })}
          placeholder={translate('attendance:monthlyCutCreate.placeholders.month')}
          color={errors.month ? 'error' : 'default'}
          supportingText={errors.month?.message
            ? translate(errors.month.message)
            : undefined}
          data-testid="monthly-cut-month-selector"
        />
        <DatePicker
          label={translate('attendance:monthlyCutCreate.fields.startDate')}
          required
          value={startDate || undefined}
          onChange={(date) => form.setValue('startDate' as Path<T>, date as never, { shouldDirty: true })}
          maxDate={endDate || undefined}
          color={errors.startDate ? 'error' : undefined}
          supportingText={errors.startDate?.message
            ? translate(errors.startDate.message)
            : undefined}
          data-testid="monthly-cut-start-date"
        />
        <DatePicker
          label={translate('attendance:monthlyCutCreate.fields.endDate')}
          required
          value={endDate || undefined}
          onChange={(date) => form.setValue('endDate' as Path<T>, date as never, { shouldDirty: true })}
          minDate={startDate || undefined}
          color={errors.endDate ? 'error' : undefined}
          supportingText={errors.endDate?.message
            ? translate(errors.endDate.message)
            : undefined}
          data-testid="monthly-cut-end-date"
        />
      </FormLayout.Section>
    </FormLayout>
  );
}

export default CreateMonthlyCutGeneralForm;
